import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import WelcomePage from "./pages/WelcomePage";
import PublicApplyPage from "./pages/PublicApplyPage";
import PublicTrophyPage from "./pages/PublicTrophyPage";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import JobsPage from "./pages/JobsPage";
import ReferralFormPage from "./pages/ReferralFormPage";
import MyReferralsPage from "./pages/MyReferralsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import NotificationsPage from "./pages/NotificationsPage";
import BadgesPage from "./pages/BadgesPage";
import RecruiterPipelinePage from "./pages/RecruiterPipelinePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AdminPage from "./pages/AdminPage";
import PayoutPage from "./pages/PayoutPage";
import SpotlightPage from "./pages/SpotlightPage";
import SettingsPage from "./pages/SettingsPage";
import ShoutOutsPage from "./pages/ShoutOutsPage";
import RivalryPage from "./pages/RivalryPage";
import HallOfFamePage from "./pages/HallOfFamePage";
import PermissionsModal from "./components/PermissionsModal";
import ForcePasswordChange from "./components/ForcePasswordChange";
import { Toaster } from "sonner";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-display font-black text-2xl">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function FirstLoginGate() {
  const { user } = useAuth();
  if (!user || user.role !== "employee") return null;
  if (user.permissions_set) return null;
  return <PermissionsModal />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/apply/:jobId" element={<PublicApplyPage />} />
      <Route path="/u/:userId" element={<PublicTrophyPage />} />
      <Route path="/hall-of-fame" element={<HallOfFamePage />} />
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="dashboard" element={<EmployeeDashboard />} />
                <Route path="jobs" element={<JobsPage />} />
                <Route path="refer" element={<ReferralFormPage />} />
                <Route path="my-referrals" element={<MyReferralsPage />} />
                <Route path="leaderboard" element={<LeaderboardPage />} />
                <Route path="rivalry" element={<RivalryPage />} />
                <Route path="payout" element={<PayoutPage />} />
                <Route path="spotlight" element={<SpotlightPage />} />
                <Route path="badges" element={<BadgesPage />} />
                <Route path="shoutouts" element={<ShoutOutsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="pipeline" element={
                  <ProtectedRoute roles={["admin"]}><RecruiterPipelinePage /></ProtectedRoute>
                } />
                <Route path="analytics" element={
                  <ProtectedRoute roles={["admin"]}><AnalyticsPage /></ProtectedRoute>
                } />
                <Route path="admin" element={
                  <ProtectedRoute roles={["admin"]}><AdminPage /></ProtectedRoute>
                } />
                <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <FirstLoginGate />
          <Toaster
            position="top-right"
            offset="72px"
            toastOptions={{
              style: { border: "2px solid #09090B", borderRadius: 16, boxShadow: "4px 4px 0 0 #09090B", fontWeight: 600 },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
